import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { AccountService } from 'src/app/core/services/account.service';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { OneFiConfirmOtpData, OneFiDirectAmount, OneFiService } from 'src/app/core/services/onefi.service';
import swal from 'sweetalert2';

@Component({
  selector: 'app-onefi-book-loan',
  templateUrl: './book-loan.component.html',
  styleUrls: ['./book-loan.component.scss']
})
export class BookLoanComponent implements OnInit, OnDestroy {
  // Loading & State flags
  isInitializing: boolean = true;
  isSendingOtp: boolean = false;
  isVerifyingOtp: boolean = false;
  isOtpSent: boolean = false;
  isConfirmed: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';

  // Transaction details
  transactionId: string = '';
  orderNumber: string = '';
  loanAmount: number | null = null;
  directAmount: OneFiDirectAmount | null = null;
  mobileNumber: string = '';
  maskedMobileNumber: string = '';

  // OTP
  otpValue: string = '';
  public otpInputConfig = {
    length: 6,
    allowNumbersOnly: true,
    showError: true,
    containerStyles: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      margin: '10px 0'
    },
    inputStyles: {
      width: '42px',
      height: '48px',
      'border-radius': '8px',
      border: '1px solid #cce3ec',
      'background-color': '#f8fcfe',
      'font-size': '1.3rem',
      'font-weight': '600',
      'text-align': 'center',
      color: '#002d3a'
    },
    disableAutoFocus: false,
  };

  // Timer
  resendCountdown: number = 30;
  canResend: boolean = false;
  private resendTimer: any = null;

  // Confirmation data
  confirmData: OneFiConfirmOtpData | null = null;

  constructor(
    private oneFiService: OneFiService,
    private accountService: AccountService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.spinner.hide();
    this.extractSessionDetails();
    this.startDirectFlow();
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
    this.spinner.hide();
  }

  private clearResendTimer(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  private extractSessionDetails(): void {
    // 1. Mobile number
    const userJson = MemoryStorageService.getItem('user');
    if (userJson) {
      try {
        const u = JSON.parse(userJson);
        this.mobileNumber = u?.mobileNo || u?.mobileNumber || u?.phoneNumber || '';
      } catch (e) {
        console.warn('Failed to parse user from storage', e);
      }
    }
    if (!this.mobileNumber && this.accountService.currentUser$) {
      this.accountService.currentUser$.subscribe((u) => {
        if (u?.mobileNumber) {
          this.mobileNumber = u.mobileNumber;
          this.maskMobile();
        }
      });
    }
    this.maskMobile();

    // 2. Loan Amount
    const storedLoanAmount = MemoryStorageService.getItem('loanAmount');
    if (storedLoanAmount) {
      this.loanAmount = parseFloat(storedLoanAmount) || null;
    }

    // 3. Stored Transaction Id if already created
    this.transactionId = MemoryStorageService.getItem('oneFiTransactionId') || MemoryStorageService.getItem('lenderApplId') || '';
  }

  private maskMobile(): void {
    if (!this.mobileNumber) {
      this.maskedMobileNumber = '';
      return;
    }
    const clean = this.mobileNumber.replace(/\D/g, '');
    if (clean.length >= 10) {
      const last4 = clean.slice(-4);
      this.maskedMobileNumber = `+91 ******${last4}`;
    } else {
      this.maskedMobileNumber = this.mobileNumber;
    }
  }

  async startDirectFlow(): Promise<void> {
    this.isInitializing = true;
    this.hasError = false;
    this.errorMessage = '';
    this.cdr.detectChanges();

    try {
      if (!this.transactionId) {
        const directRes = await lastValueFrom(this.oneFiService.startDirectTransaction({}));
        if (!directRes?.data?.transaction_id) {
          throw new Error(directRes?.error || 'Failed to initiate direct loan transaction with 1Fi.');
        }

        this.transactionId = directRes.data.transaction_id;
        this.orderNumber = directRes.data.order_number || '';
        this.directAmount = directRes.data.amount || null;

        if (this.directAmount?.order_amount) {
          this.loanAmount = this.directAmount.order_amount;
        }

        MemoryStorageService.setItem('oneFiTransactionId', this.transactionId);
        MemoryStorageService.setItem('lenderApplId', this.transactionId);
      }

      await this.triggerOtp();
    } catch (error: any) {
      console.error('Error starting 1Fi direct transaction:', error);
      this.ngZone.run(() => {
        this.hasError = true;
        this.errorMessage = error?.message || 'Failed to initiate 1Fi transaction. Please try again.';
        this.spinner.hide();
        this.cdr.detectChanges();
      });
      swal.fire('Error', this.errorMessage, 'error');
    } finally {
      this.ngZone.run(() => {
        this.isInitializing = false;
        this.spinner.hide();
        this.cdr.detectChanges();
      });
    }
  }

  async triggerOtp(): Promise<void> {
    if (!this.transactionId) {
      swal.fire('Error', 'Transaction session expired. Please restart.', 'error');
      return;
    }

    this.isSendingOtp = true;
    try {
      const otpRes = await lastValueFrom(this.oneFiService.sendOtp(this.transactionId));
      if (otpRes?.error) {
        throw new Error(otpRes.error);
      }

      this.ngZone.run(() => {
        this.isOtpSent = true;
        this.isInitializing = false;
        if (otpRes?.data?.transaction_id) {
          this.transactionId = otpRes.data.transaction_id;
          MemoryStorageService.setItem('oneFiTransactionId', this.transactionId);
          MemoryStorageService.setItem('lenderApplId', this.transactionId);
        }
        if (otpRes?.data?.mobile) {
          this.mobileNumber = otpRes.data.mobile;
          this.maskMobile();
        }

        this.startResendTimer();
        this.otpValue = '';
        this.spinner.hide();
        this.cdr.detectChanges();
      });
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      this.ngZone.run(() => {
        this.errorMessage = error?.message || 'Failed to send OTP. Please try again.';
        this.spinner.hide();
        this.cdr.detectChanges();
      });
      swal.fire('Error', this.errorMessage, 'error');
    } finally {
      this.ngZone.run(() => {
        this.isSendingOtp = false;
        this.isInitializing = false;
        this.spinner.hide();
        this.cdr.detectChanges();
      });
    }
  }

  async resendOtp(): Promise<void> {
    if (!this.canResend || this.isSendingOtp) return;
    this.spinner.show();
    await this.triggerOtp();
    this.spinner.hide();
    if (this.isOtpSent) {
      swal.fire({
        icon: 'success',
        title: 'OTP Resent',
        text: 'A new verification code has been sent to your mobile number.',
        timer: 2000,
        showConfirmButton: false
      });
    }
  }

  private startResendTimer(): void {
    this.clearResendTimer();
    this.resendCountdown = 30;
    this.canResend = false;

    this.resendTimer = setInterval(() => {
      this.ngZone.run(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) {
          this.canResend = true;
          this.clearResendTimer();
        }
        this.cdr.detectChanges();
      });
    }, 1000);
  }

  onOtpChange(otp: string): void {
    this.otpValue = otp;
  }

  async confirmLoanOtp(): Promise<void> {
    if (!this.otpValue || this.otpValue.length < 4) {
      swal.fire('Invalid OTP', 'Please enter the complete verification code.', 'warning');
      return;
    }

    if (!this.transactionId) {
      swal.fire('Error', 'Session expired. Please restart the process.', 'error');
      return;
    }

    this.isVerifyingOtp = true;
    this.spinner.show();
    this.cdr.detectChanges();

    try {
      const confirmRes = await lastValueFrom(this.oneFiService.confirmOtp(this.transactionId, this.otpValue));
      if (confirmRes?.error) {
        throw new Error(confirmRes.error);
      }

      if (confirmRes?.data) {
        this.ngZone.run(() => {
          this.confirmData = confirmRes.data;
          this.isConfirmed = true;
          this.isVerifyingOtp = false;
          this.spinner.hide();
          this.cdr.detectChanges();
        });

        swal.fire({
          icon: 'success',
          title: 'Loan Confirmed!',
          text: `Your loan ${this.confirmData.loan_number || ''} has been successfully booked.`,
          timer: 3000,
          showConfirmButton: false
        });

        setTimeout(() => {
          this.navigateToSuccess();
        }, 3000);
      } else {
        throw new Error('Unable to confirm OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('Error confirming OTP:', error);
      this.ngZone.run(() => {
        this.isVerifyingOtp = false;
        this.spinner.hide();
        this.cdr.detectChanges();
      });
      swal.fire('Verification Failed', error?.message || 'Invalid OTP. Please check and try again.', 'error');
    } finally {
      this.ngZone.run(() => {
        this.isVerifyingOtp = false;
        this.spinner.hide();
        this.cdr.detectChanges();
      });
    }
  }

  navigateToSuccess(): void {
    this.router.navigate(['/assist-journey/assist-success']);
  }

  retryInit(): void {
    this.startDirectFlow();
  }
}
